import {
  Routes,
  Route,
  useLocation,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import Layout from "./components/layout";
import "./resources/styles/app.scss";
import React, { useCallback, useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import ReactGA from "react-ga4";
import {
  getLocalCompanyId,
  getLocalUser,
} from "./components/functions/utilities";
import { setSentryUser, clearSentryUser } from "./config/sentry";
import ReportDetailsProvider from "./pages/ReportDetails/context/ReportContext";
import DashboardProvider from "./pages/Dashboard/context/DashboardContext";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";

const TRACKING_ID = `${import.meta.env.VITE_GA_TRACKING_ID}`;

// ReactGA.initialize(TRACKING_ID, { gaOptions: { siteSpeedSampleRate: 100 } });

ReactGA.initialize(TRACKING_ID, {
  gtagOptions: {
    custom_map: {
      dimension21: 0, // userId
      metric21: 1, // Login times
    },
  },
});

const GSTReconciliation = React.lazy(
  () => import("./pages/GSTReconciliation/GSTReconciliationIndex")
);

const PulseIndexComponent = React.lazy(
  () => import("./pages/Pulse/PulseIndexComponent")
);

const ReportList = React.lazy(() => import("./pages/ReportList/ReportList"));

const ReportSummary = React.lazy(
  () => import("./pages/NewReportSummary/NewReportSummary")
);

const ReportDetailsPage = React.lazy(
  () => import("./pages/ReportDetails/ReportDetails")
);

const Dashboard = React.lazy(() => import("./pages/Dashboard/Dashboard"));

const FileUploadCard = React.lazy(
  () => import("./pages/CreateReport/Components/FileUploadCard/FileUploadCard")
);

const UpdateDataDump = React.lazy(
  () => import("./pages/UpdateDataDump/UpdateDataDump")
);

const CollaborationHubIndex = React.lazy(
  () => import("./pages/CollaborationHub/CollaborationHubIndex")
);

const ChartSelection = React.lazy(
  () => import("./pages/CreateReport/ChartSelection/ChartSelection")
);

const ReportSuccess = React.lazy(
  () => import("./pages/ReportSuccess/ReportSuccess")
);

const WorkspaceStore = React.lazy(
  () => import("./pages/WorkspaceStore/WorkspaceStore")
);
const DelegateSetupPage = React.lazy(
  () => import("./pages/ConnectionHub/components/delegate/DelegateSetupPage/DelegateSetupPage")
);

const Preview = React.lazy(() => import("./pages/Activate/Components/Preview"));

const Support = React.lazy(() => import("./pages/Support/Support"));

const UserManagement = React.lazy(
  () => import("./pages/UserManagement/UserManagement")
);

// const PdfViewer = React.lazy(() => import("./pages/reportTemplate/pdfViewer"));

const DecisionHub = React.lazy(() => import("./pages/DecisionHub/DecisionHub"));
const Decision = React.lazy(
  () => import("./pages/DecisionHub/Decision/Decision")
);

// import SignIn from "./pages/SignIn/SignIn";
const SignIn = React.lazy(() => import("./pages/SignIn/SignIn"));
const SignUp = React.lazy(() => import("./pages/SignUp/SignUp"));
const MagicLogin = React.lazy(() => import("./pages/MagicLogin/MagicLogin"));
const ExpiredLinkPage = React.lazy(
  () => import("./pages/ExpiredLinkPage/ExpiredLinkPage")
);
const ForgotPasswordRequest = React.lazy(
  () =>
    import("./pages/ForgotPassword/ResetPasswordRequest/ForgotPasswordRequest")
);
const ForgotPassword = React.lazy(
  () => import("./pages/ForgotPassword/FogotPassword")
);
import PageNotFound from "./pages/PageNotFound/PageNotFound";
import TechnicalErrorBoundary from "./components/common/ErrorBoundary/TechnicalErrorBoundary";
import DashboardErrorBoundary from "./components/common/ErrorBoundary/DashboardErrorBoundary";
import {
  IIntegrationPartner,
  UserRole,
  disbursementRoutes,
  monthlyReportRoutes,
  underwritingRoutes,
} from "./interfaces/interfaces";
import {
  setDataLimitationConfig,
  setDataRestrictionConfig,
  setIntegrationPartners,
  setWorkspaceRestrictionsConfig,
  setSIPromoConfig,
} from "./redux/config/config";
import { useAppDispatch } from "./redux/reduxHooks";
import { IpRestS } from "./rest/ipRest-service";
import FinanceStatement from "./pages/Finance/statement/FinanceStatement";
import FinanceSections from "./pages/Finance/financeSections/FinanceSection";
import PcSections from "./pages/Finance/pcSections/PcSections";
import PcHome from "./pages/Finance/home/PcHome";
import PcSectionWrapper from "./pages/Finance/pcSections/PcSectionWrapper";
import UnderwritingHome from "./pages/Underwriting/UnderwritingHome/UnderwritingHome";
import UnderwritingSections from "./pages/Underwriting/UnderwritingSections/UnderwritingSections";
import UnderWritingFormProvider from "./pages/Underwriting/context/UnderwritingFormContext";
import { UnderwritingBankStatementsProvider } from "./pages/Underwriting/UnderwritingBankStatementsProvider";
import PulseProvider from "./pages/Pulse/context/PulseContext";
import UnderwritingStatus from "./pages/Underwriting/UnderwritingStatus/UnderwritingStatus";
import DisbursementSections from "./pages/Disbursement/DisbursementSections";
import DisbursementFormProvider from "./pages/Disbursement/context/DisbursementFormContext";
import DisbursementSuccess from "./pages/Disbursement/successPage/DisbursementSuccess";
import DisbursementExpired from "./pages/Disbursement/ExpiredPage/DisbursementExpired";
import { RestS } from "./rest/rest-service";
import MonthlyReportHome from "./pages/MonthlyReport/MonthlyReportHome/MonthlyReportHome";
import MonthlyReportFromProvider from "./pages/MonthlyReport/context/MonthlyReportFormContext";
import MonthlyReportSections from "./pages/MonthlyReport/MonthlyReportSections/MonthlyReportSections";
import BrandInviteStatus from "./pages/BrandInviteStatus/BrandInviteStatus";
import BrandPage from "./pages/Dashboard/Components/AgencyDashboardTabs/BrandPage/BrandPage";
import SFPLLandingPage from "./pages/SFPL/SFPLLandingPage";
import BrandSummary from "./pages/Dashboard/Components/AgencyDashboardTabs/AgencyDashboardTabs";

const Router = [
  {
    path: "/Reports",
    component: <ReportList />,
    index: true,
  },
  {
    path: "/ReportSummary/:reportId",
    component: <ReportSummary />,
  },
  {
    path: "/ReportDetails/:reportId",
    component: (
      <ReportDetailsProvider>
        <ReportDetailsPage />
      </ReportDetailsProvider>
    ),
  },
  {
    path: "/CreateReport/:reportId",
    component: <FileUploadCard />,
  },
  {
    path: "/ModifyReport/:reportId",
    component: <FileUploadCard />,
  },
  {
    path: "/UpdateDataDump/:reportId",
    component: <UpdateDataDump />,
  },
  // {
  //   path: "/collaboarationHub",
  //   component: <CollaborationHubIndex />,
  // },
  {
    path: "/ChartSelection/:reportId",
    component: <ChartSelection />,
  },
  {
    path: "/ReportSuccess",
    component: <ReportSuccess />,
  },
  {
    path: "/preview",
    component: <Preview />,
  },
  // {
  //   path: "/AnalyseTrend/:reportVersionId/:Identifier",
  //   component: <AnalyseTrend />,
  // },
  {
    path: "/support",
    component: <Support />,
  },
  // {
  //   path: "/",
  //   component: (
  //     <TechnicalErrorBoundary>
  //       <DashboardProvider>
  //         <Dashboard />
  //       </DashboardProvider>
  //     </TechnicalErrorBoundary>
  //   ),
  // },
];

function App() {
  let location = useLocation();
  const [userRoute, setUserRoute] = useState(Router);
  const user = getLocalUser();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let path = location.pathname.split("/")[1];
    const companyId = getLocalCompanyId();
    if (companyId != import.meta.env.VITE_MERITO_COMPANY_ID) {
      // ReactGA.pageview("/" + path);
      // ReactGA.send({ hitType: "PageView", page: "/" + path, title: document.title });
    }
    if (user) {
      // console.log("running user preset", user);
      ReactGA.set({
        dimension1: user.id.toString(),
        dimension2: user.companyId.toString(),
        dimension3: user["companyName"] ? user.companyName.toString() : "",
      });
      // Set Sentry user context for error tracking
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name,
        companyId: user.companyId,
        role: user.role,
      });
    } else {
      clearSentryUser();
    }
  }, [location, user]);

  useEffect(() => {
    if (user?.role == "User") {
      const allRoutes = [
        {
          path: "/supplier-intelligence/*",
          component: (
            <TechnicalErrorBoundary>
              <GSTReconciliation />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/pulse/*",
          component: (
            <TechnicalErrorBoundary>
              <PulseProvider>
                <PulseIndexComponent />
              </PulseProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/workspaceStore/*",
          component: <WorkspaceStore />,
        },
        {
          path: "/boost",
          component: (
            <TechnicalErrorBoundary>
              <DecisionHub />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/boost/:decisionId",
          component: (
            <TechnicalErrorBoundary>
              <Decision />
            </TechnicalErrorBoundary>
          ),
        },
        // {
        //   path: "/pnltracker",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <PcHome />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        // {
        //   path: "/pnltracker/edit/*",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <PcSectionWrapper />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        // {
        //   path: "/pnltracker/statement",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <FinanceStatement />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        {
          path: `${underwritingRoutes.underwritingHome}`,
          component: (
            <TechnicalErrorBoundary>
              <UnderwritingHome />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${underwritingRoutes.underwritingStatus}`,
          component: (
            <TechnicalErrorBoundary>
              <UnderwritingStatus />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${underwritingRoutes.underwritingApplication}/*`,
          component: (
            <TechnicalErrorBoundary>
              <UnderWritingFormProvider>
                <UnderwritingBankStatementsProvider>
                  <UnderwritingSections />
                </UnderwritingBankStatementsProvider>
              </UnderWritingFormProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${monthlyReportRoutes.monthlyReportHome}`,
          component: (
            <TechnicalErrorBoundary>
              <MonthlyReportHome />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${monthlyReportRoutes.monthlyReportApplication}/*`,
          component: (
            <TechnicalErrorBoundary>
              <MonthlyReportFromProvider>
                <MonthlyReportSections />
              </MonthlyReportFromProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/${disbursementRoutes.disbursementSuccess}/:formId`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementSuccess />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/${disbursementRoutes.disbursementExpired}/:formId`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementExpired />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/*`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementSections />
            </TechnicalErrorBoundary>
          ),
        },
      ];
      setUserRoute([...userRoute, ...allRoutes]);
    }
    if (user?.role == "Admin") {
      const otherRoutes = [
        {
          path: "/supplier-intelligence/*",
          component: (
            <TechnicalErrorBoundary>
              <GSTReconciliation />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/pulse/*",
          component: (
            <TechnicalErrorBoundary>
              <PulseProvider>
                <PulseIndexComponent />
              </PulseProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/Settings",
          component: <UserManagement />,
        },
        {
          path: "/workspaceStore/*",
          component: <WorkspaceStore />,
        },
        {
          path: "/boost",
          component: (
            <TechnicalErrorBoundary>
              <DecisionHub />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: "/boost/:decisionId",
          component: (
            <TechnicalErrorBoundary>
              <Decision />
            </TechnicalErrorBoundary>
          ),
        },
        // {
        //   path: "/pnltracker",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <PcHome />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        // {
        //   path: "/pnltracker/edit/*",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <PcSectionWrapper />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        // {
        //   path: "/pnltracker/statement",
        //   component: (
        //     <TechnicalErrorBoundary>
        //       <FinanceStatement />
        //     </TechnicalErrorBoundary>
        //   ),
        // },
        {
          path: `${underwritingRoutes.underwritingHome}`,
          component: (
            <TechnicalErrorBoundary>
              <UnderwritingHome />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${underwritingRoutes.underwritingStatus}`,
          component: (
            <TechnicalErrorBoundary>
              <UnderwritingStatus />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${underwritingRoutes.underwritingApplication}/*`,
          component: (
            <TechnicalErrorBoundary>
              <UnderWritingFormProvider>
                <UnderwritingBankStatementsProvider>
                  <UnderwritingSections />
                </UnderwritingBankStatementsProvider>
              </UnderWritingFormProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${monthlyReportRoutes.monthlyReportHome}`,
          component: (
            <TechnicalErrorBoundary>
              <MonthlyReportHome />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${monthlyReportRoutes.monthlyReportApplication}/*`,
          component: (
            <TechnicalErrorBoundary>
              <MonthlyReportFromProvider>
                <MonthlyReportSections />
              </MonthlyReportFromProvider>
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/${disbursementRoutes.disbursementSuccess}/:formId`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementSuccess />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/${disbursementRoutes.disbursementExpired}/:formId`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementExpired />
            </TechnicalErrorBoundary>
          ),
        },
        {
          path: `${disbursementRoutes.disbursementSections}/:formId/*`,
          component: (
            <TechnicalErrorBoundary>
              <DisbursementFormProvider>
                <DisbursementSections />
              </DisbursementFormProvider>
            </TechnicalErrorBoundary>
          ),
        },
      ];

      setUserRoute([...userRoute, ...otherRoutes]);
    }

    if (user?.role == "Agency") {
      const otherRoutes = [
        {
          path: "/Settings",
          component: <UserManagement />,
        },
        {
          path: "/workspaceStore/*",
          component: <WorkspaceStore />,
        },
        {
          path: "/dashboard/*",
          component: (
            <TechnicalErrorBoundary>
              <BrandSummary />
            </TechnicalErrorBoundary>
          )
        }
        // {
        //   path: "/brand/:brandId",
        //   component: <BrandPage />,
        // },
      ];
      setUserRoute([...userRoute, ...otherRoutes]);
    }

    console.log("user", user);
    if (user) {
      fetchAndSetDataLimitation();
      fetchCompanyDataRestrictions();
      fetchWorkspaceRestrictions();
      fetchAndSetSIPromoConfig();
    }

    setLoading(false);
  }, [user?.role]);

  const fetchAndSetDataLimitation = async () => {
    try {
      const config: any = await RestS.fetchDatalimitationAppConfig();
      // console.log('datalimitationConfig', config.data);
      if (config?.success) {
        dispatch(setDataLimitationConfig(config.data));
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchAndSetSIPromoConfig = async () => {
    try {
      const config: any = await RestS.fetchSIPromoConfig();
      if (config?.success) {
        dispatch(setSIPromoConfig(config.data));
      }
    } catch {
      // fetch failure → all promos show by default (siPromoConfig stays {})
    }
  };

  const fetchCompanyDataRestrictions = async () => {
    try {
      const config: any = await RestS.fetchCompanyDataRestrictions();
      if (config?.success) {
        dispatch(setDataRestrictionConfig(config.data));
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchWorkspaceRestrictions = async () => {
    try {
      const config: any = await RestS.fetchWorkspaceRestrictionsConfig();
      if (config?.success) {
        dispatch(setWorkspaceRestrictionsConfig(config.data));
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const fallbackFn = useCallback(() => {
    return <></>;
  }, []);

  useEffect(() => {
    const getIPHandler = (ips: IIntegrationPartner[]) => {
      dispatch(
        setIntegrationPartners(
          ips.reduce(
            (
              result: Record<string, IIntegrationPartner>,
              item: IIntegrationPartner
            ) => {
              if (item?.combined === false && item?.logo && item?.companyName)
                result[item.companyName] = item;
              return result;
            },
            {}
          )
        )
      );
    };

    Promise.all([IpRestS.getAllIp().then(getIPHandler)]);
  }, []);

  // useEffect(() => {
  //   if (user) {
  //     ReactGA.set({
  //       [GADimensions.userIdUL]: user.id,
  //       [GADimensions.userNameUL]: user.name,
  //       [GADimensions.userTypeUL]: user.role,
  //       [GADimensions.companyIdUL]: user.companyId,
  //       [GADimensions.companyNameUL]: user.companyName,
  //     });
  //   }
  // }, []);
  // console.log('userRoute', userRoute);
  return (
    // <>
    //   {!loading ? (
    //     <Routes>
    //       <Route path="/" element={<Layout />}>
    //         {userRoute.map((item, index) => (
    //           <Route
    //             key={index}
    //             path={item.path}
    //             index={item.index}
    //             element={
    //               <React.Suspense fallback={fallbackFn()}>
    //                 {item.component}
    //               </React.Suspense>
    //             }
    //           />
    //         ))}
    //         <Route path="*" element={<PageNotFound />} />
    //       </Route>
    <>
      {!loading ? (
        <Routes>
          <Route
            path="/connect/setup/:token"
            element={<React.Suspense fallback={fallbackFn()}><DelegateSetupPage /></React.Suspense>}
          />
          <Route path="/" element={<Layout />}>
            {userRoute.map((item, index) => (
              <Route
                key={index}
                path={item.path}
                index={item.index}
                element={
                  <React.Suspense fallback={fallbackFn()}>
                    {item.component}
                  </React.Suspense>
                }
              />
            ))}
            <Route path="/" element={user?.role == UserRole.Agency ? <Navigate to="/dashboard" /> :
              <React.Suspense fallback={fallbackFn()}>
                <DashboardErrorBoundary>
                  <DashboardProvider>
                    <Dashboard />
                  </DashboardProvider>
                </DashboardErrorBoundary>
              </React.Suspense>}
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>

          {/* <Route
          path="/workspaceStore/*"
          element={
            user?.role !== "Agency" ? (
              <React.Suspense fallback={fallbackFn()}>
                <WorkspaceStore />
              </React.Suspense>
            ) : (
              <PageNotFound />
            )
          }
        /> */}
          <Route
            path="/login"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <SignIn />
              </React.Suspense>
            }
          />
          <Route path="/brandinvite" element={<BrandInviteStatus />} />
          <Route path="/sfpl" element={<SFPLLandingPage />} />
          <Route
            path="/register"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <SignUp title="Register" />
              </React.Suspense>
            }
          />
          <Route
            path="/auth/magic"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <MagicLogin />
              </React.Suspense>
            }
          />
          <Route
            path="/expired/:type/:id"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <ExpiredLinkPage title="Expired Link" />
              </React.Suspense>
            }
          />
          <Route
            path="/password/forgot/request"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <ForgotPasswordRequest title="Forgot Password" />
              </React.Suspense>
            }
          />
          <Route
            path="/password/forgot/:userId/:token"
            element={
              <React.Suspense fallback={fallbackFn()}>
                <ForgotPassword title="Forgot Password" />
              </React.Suspense>
            }
          />
        </Routes>
      ) : (
        <></>
      )}
    </>
  );
}

export default App;
